import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectMembers } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';

const EXPENSE_CATEGORIES = ['food', 'transport', 'housing', 'entertainment', 'health', 'education', 'shopping', 'services', 'credit', 'other'];
const INCOME_CATEGORIES = ['salary', 'freelance', 'other'];

export default function BulkEditModal({ open, onClose, transactionIds }) {
  const { t } = useTranslation();
  const transactions = useStore((s) => s.transactions);
  const bulkUpdateTransactions = useStore((s) => s.bulkUpdateTransactions);
  const members = useStore(selectMembers);

  const [category, setCategory] = useState(null);
  const [memberAction, setMemberAction] = useState('no_change'); // 'no_change' | 'set'
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  const selectedTxs = useMemo(
    () => transactions.filter((tx) => transactionIds.includes(tx.id)),
    [transactions, transactionIds]
  );

  const types = useMemo(() => new Set(selectedTxs.map((tx) => tx.type)), [selectedTxs]);
  const isSameType = types.size === 1;
  const singleType = isSameType ? [...types][0] : null;
  const allExpenses = singleType === 'expense';
  const categories = singleType === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const handleApply = () => {
    const data = {};
    if (category) data.category = category;
    if (memberAction === 'set') data.memberIds = selectedMemberIds;

    if (Object.keys(data).length > 0) {
      bulkUpdateTransactions(transactionIds, data);
    }
    handleClose();
  };

  const handleClose = () => {
    setCategory(null);
    setMemberAction('no_change');
    setSelectedMemberIds([]);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('transactions.bulkEditTitle')} footer={
      <Button className="w-full" onClick={handleApply}>
        {t('transactions.bulkEditApply')}
      </Button>
    }>
      <div className="space-y-5">
        {/* Category */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 block">
            {t('transactions.bulkEditCategory')}
          </label>
          {isSameType ? (
            <div className="grid grid-cols-3 gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(category === c ? null : c)}
                  className={`py-2 px-3 text-xs font-medium rounded-xl border transition-all flex flex-col items-center gap-1 ${
                    category === c
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                  }`}
                >
                  <span>{CATEGORY_ICONS[c] || '\uD83D\uDCCC'}</span>
                  {t(`categories.${c}`)}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">
              {t('transactions.bulkEditMixed')}
            </p>
          )}
        </div>

        {/* Member picker */}
        {members.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 block">
              {t('transactions.bulkEditMember')}
            </label>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setMemberAction('no_change')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  memberAction === 'no_change'
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                }`}
              >
                {t('transactions.bulkEditNoChange')}
              </button>
              <button
                onClick={() => setMemberAction('set')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  memberAction === 'set'
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                }`}
              >
                {t('transactions.bulkEditSetMembers')}
              </button>
            </div>
            {memberAction === 'set' && (
              <MemberMultiSelect
                value={selectedMemberIds}
                onChange={setSelectedMemberIds}
              />
            )}
          </div>
        )}

      </div>
    </Modal>
  );
}
